import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Egg, Home, AlertTriangle, TrendingUp, Activity,
  ChevronRight, ShoppingCart, CloudRain,
} from 'lucide-react';
import { eggWatchAdminAPI, EggWatchAdminSummary } from '../../../services/eggWatchAdminAPI';
import EggWatchFeedPerformanceTable from './EggWatchFeedPerformanceTable';
import EggWatchActivityFeed from './EggWatchActivityFeed';
import './EggWatchAdmin.css';

const EggWatchAdminDashboard: React.FC = () => {
  const [data, setData] = useState<EggWatchAdminSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await eggWatchAdminAPI.getSummary();
      setData(res.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load Egg Watch dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="ewa-loading-state">Loading Egg Watch intelligence...</div>;

  if (error) {
    return (
      <div className="egg-watch-admin">
        <div className="ewa-header">
          <div className="ewa-header-title">
            <Egg className="ewa-header-icon" />
            <div>
              <h1>Smart Egg Watch</h1>
              <p className="ewa-header-subtitle">Admin Intelligence Dashboard</p>
            </div>
          </div>
        </div>
        <div className="ewa-empty-state">
          <p>{error}</p>
          <button className="ewa-btn-primary" onClick={loadDashboard}>Retry</button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { program_health: ph, alerts: al, opportunities: opp } = data;

  return (
    <div className="egg-watch-admin">
      {/* Header */}
      <div className="ewa-header">
        <div className="ewa-header-title">
          <Egg className="ewa-header-icon" />
          <div>
            <h1>Smart Egg Watch</h1>
            <p className="ewa-header-subtitle">Admin Intelligence Dashboard</p>
          </div>
        </div>
        <div className="ewa-header-actions">
          <Link to="/planner/egg-watch/farms" className="ewa-btn-secondary">
            <Home size={16} /> All Farms
          </Link>
          <Link to="/planner/egg-watch/alerts" className="ewa-btn-secondary">
            <AlertTriangle size={16} /> Alerts
          </Link>
          <Link to="/planner/egg-watch/export" className="ewa-btn-secondary">
            Export
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="ewa-stats-grid">
        <div className="ewa-stat-card">
          <div className="ewa-stat-icon farms"><Home size={24} /></div>
          <div>
            <div className="ewa-stat-value">{ph.active_farms}</div>
            <div className="ewa-stat-label">Active Farms</div>
          </div>
        </div>
        <div className="ewa-stat-card">
          <div className="ewa-stat-icon logged"><Activity size={24} /></div>
          <div>
            <div className="ewa-stat-value">
              {ph.logged_today}
              <span className="ewa-stat-pct">({ph.logging_rate_pct.toFixed(0)}%)</span>
            </div>
            <div className="ewa-stat-label">Logged Today</div>
          </div>
        </div>
        <div className="ewa-stat-card">
          <div className="ewa-stat-icon hens"><Egg size={24} /></div>
          <div>
            <div className="ewa-stat-value">{ph.total_hens_tracked.toLocaleString()}</div>
            <div className="ewa-stat-label">Hens Tracked</div>
          </div>
        </div>
        <div className="ewa-stat-card">
          <div className="ewa-stat-icon rate"><TrendingUp size={24} /></div>
          <div>
            <div className="ewa-stat-value">
              {ph.avg_lay_rate != null ? `${ph.avg_lay_rate.toFixed(1)}%` : '—'}
            </div>
            <div className="ewa-stat-label">Avg Lay Rate</div>
          </div>
        </div>
      </div>

      {/* Alert Banners */}
      {(al.total_attention_needed > 0 || opp.total > 0) && (
        <div className="ewa-alert-banners">
          {al.production_drops > 0 && (
            <div
              className="ewa-alert-banner warning"
              onClick={() => navigate('/planner/egg-watch/alerts?type=production_drop')}
            >
              <AlertTriangle size={18} />
              <span><strong>{al.production_drops}</strong> farm{al.production_drops !== 1 ? 's' : ''} with production drops</span>
              <ChevronRight size={16} />
            </div>
          )}
          {al.not_logging > 0 && (
            <div
              className="ewa-alert-banner warning"
              onClick={() => navigate('/planner/egg-watch/alerts?type=not_logging')}
            >
              <AlertTriangle size={18} />
              <span><strong>{al.not_logging}</strong> farm{al.not_logging !== 1 ? 's' : ''} stopped logging</span>
              <ChevronRight size={16} />
            </div>
          )}
          {opp.feed_reorders > 0 && (
            <div
              className="ewa-alert-banner info"
              onClick={() => navigate('/planner/egg-watch/farms?status=attention')}
            >
              <ShoppingCart size={18} />
              <span><strong>{opp.feed_reorders}</strong> feed reorder opportunit{opp.feed_reorders !== 1 ? 'ies' : 'y'}</span>
              <ChevronRight size={16} />
            </div>
          )}
          {opp.weather_prep > 0 && (
            <div
              className="ewa-alert-banner info"
              onClick={() => navigate('/planner/egg-watch/alerts?type=weather')}
            >
              <CloudRain size={18} />
              <span><strong>{opp.weather_prep}</strong> farm{opp.weather_prep !== 1 ? 's' : ''} need weather prep</span>
              <ChevronRight size={16} />
            </div>
          )}
        </div>
      )}

      {/* Main Grid */}
      <div className="ewa-dashboard-grid">
        <div className="ewa-dashboard-main">
          {/* Feed Performance Preview */}
          <div className="ewa-card">
            <div className="ewa-card-header">
              <h2><TrendingUp size={18} /> Feed Performance</h2>
              <Link to="/planner/egg-watch/feed-performance" className="ewa-btn-link">
                Full Analysis <ChevronRight size={14} />
              </Link>
            </div>
            <div className="ewa-card-content">
              <EggWatchFeedPerformanceTable data={data.feed_performance} compact />
            </div>
          </div>
        </div>

        <div className="ewa-dashboard-sidebar">
          {/* Opportunities */}
          <div className="ewa-card">
            <div className="ewa-card-header">
              <h2>Opportunities</h2>
            </div>
            <div className="ewa-card-content">
              {opp.total === 0 ? (
                <div className="ewa-empty-state">No action items right now</div>
              ) : (
                <div className="ewa-opportunity-list">
                  {opp.feed_reorders > 0 && (
                    <div
                      className="ewa-opportunity-item"
                      onClick={() => navigate('/planner/egg-watch/farms?status=attention')}
                    >
                      <ShoppingCart size={18} color="#d97706" />
                      <div>
                        <strong>{opp.feed_reorders}</strong> feed reorder{opp.feed_reorders !== 1 ? 's' : ''}
                      </div>
                      <ChevronRight size={14} />
                    </div>
                  )}
                  {opp.weather_prep > 0 && (
                    <div
                      className="ewa-opportunity-item"
                      onClick={() => navigate('/planner/egg-watch/alerts?type=weather')}
                    >
                      <CloudRain size={18} color="#2563eb" />
                      <div>
                        <strong>{opp.weather_prep}</strong> weather prep needed
                      </div>
                      <ChevronRight size={14} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="ewa-card">
            <div className="ewa-card-header">
              <h2>Recent Activity</h2>
            </div>
            <div className="ewa-card-content">
              <EggWatchActivityFeed items={data.recent_activity} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EggWatchAdminDashboard;
